import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { Wrench } from "lucide-react";

export default function RepairEquipmentDialog({ item, allItems, onClose, onDone }) {
  const linkedParts = allItems.filter((p) => p.linked_equipment_id === item.id);
  const [selected, setSelected] = useState(new Set());
  const [note, setNote] = useState("");
  const [durationMin, setDurationMin] = useState(30); // Durata reparației — default 30 min

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
    });
  };

  const submit = async () => {
    const dm = parseInt(durationMin, 10);
    if (!dm || dm <= 0) {
      toast.error("Specifică durata reparației (minute)");
      return;
    }
    try {
      const r = await api.post(`/inventory/${item.id}/repair`, {
        spare_part_ids: [...selected],
        note,
        duration_min: dm,
      });
      if (r.data?.tagesraport) {
        toast.success(`✅ Reparat. Adăugat și în Tagesraport (${dm} min pentru ${r.data.tagesraport.person_name})`);
      } else {
        toast.success(`✅ Echipament reparat (durata: ${dm} min)`);
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const QUICK_PRESETS = [10, 15, 30, 45, 60, 90, 120, 180];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>REPARĂ: {item.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="card-flat p-3 bg-[var(--green-bg)] text-sm">
            <Wrench size={14} className="inline mr-1.5"/>După reparație, piesele selectate sunt scoase din stoc, iar echipamentul revine la status <strong>funcțional</strong>.
          </div>

          {/* DURATA REPARAȚIEI — obligatorie, folosită și pentru Tagesraport */}
          <div className="card-flat p-3 bg-amber-50 border border-amber-200 rounded-md">
            <Label className="small-label flex items-center gap-1.5">
              ⏱️ Cât a durat reparația? <span className="text-red-600">*</span>
            </Label>
            <div className="flex gap-2 items-center mt-1.5">
              <Input
                type="number"
                min={1}
                max={1440}
                value={durationMin}
                onChange={(e) => setDurationMin(e.target.value)}
                className="w-28 font-mono text-center font-bold"
                data-testid="repair-duration-input"
                placeholder="min"
              />
              <span className="text-sm text-zinc-600 font-medium">minute</span>
              <div className="flex flex-wrap gap-1 ml-2">
                {QUICK_PRESETS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDurationMin(m)}
                    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded border transition-colors ${
                      Number(durationMin) === m
                        ? "bg-amber-500 text-white border-amber-600"
                        : "bg-white text-zinc-600 border-zinc-300 hover:border-amber-400 hover:text-amber-700"
                    }`}
                    data-testid={`repair-duration-preset-${m}`}
                  >
                    {m}m
                  </button>
                ))}
              </div>
            </div>
            <div className="text-[10px] text-amber-700 mt-1.5 italic">
              💡 Dacă tu (Khreis Christian sau Serghei Istratenco) ai făcut reparația, va fi adăugată automat în Tagesraport.
            </div>
          </div>

          <div>
            <Label className="small-label flex items-center gap-1.5">
              Piese folosite <span className="text-[10px] text-zinc-500 font-normal normal-case italic">(opțional — {selected.size} selectate)</span>
            </Label>
            {linkedParts.length === 0 ? (
              <div className="text-sm text-zinc-500 italic mt-2 p-2 bg-zinc-50 border border-zinc-200 rounded">
                Reparația poate fi efectuată și fără piese — completează doar nota cu ce s-a făcut.
              </div>
            ) : (
              <div className="mt-2 max-h-60 overflow-y-auto border border-[var(--border)] rounded-md divide-y divide-[var(--border)]">
                {linkedParts.map((p) => (
                  <label key={p.id} className="flex items-center gap-3 p-2.5 hover:bg-zinc-50 cursor-pointer">
                    <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{p.name}</div>
                      <div className="text-xs text-zinc-500">Stoc: {p.quantity || 1}{p.unit ? ` ${p.unit}` : ""}{p.location ? ` · ${p.location}` : ""}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div><Label className="small-label">Notă (ce s-a reparat exact?)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" rows={2} placeholder="Ex: înlocuit cablu de alimentare, strâns conexiuni..." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} className="bg-[var(--green-valid)] text-white hover:opacity-90" data-testid="submit-equipment-repair"><Wrench size={14} className="mr-1"/>Înregistrează</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
